import React, {
  forwardRef,
  useCallback,
  useRef,
  useState,
  useEffect,
} from "react";
import { Field, FastField, useField, useFormikContext } from "formik";
import { Flex, Box } from "reflexbox/styled-components";
import { Body, Helper } from "../../Text";
import Label from "../Label";
import styled, { useTheme } from "styled-components";
import { ShimmerField } from "../../Shimmer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import PhoneInput from "react-phone-number-input";
import Card from "../../Card";

import Select from 'react-select';

// import DatalistInput from "react-datalist-input";
// import 'react-datalist-input/dist/styles.css';

const ToolTip = styled(Box)`
  position: absolute;
  top: 1rem;
  right: 0.5rem;
  cursor: pointer;

  z-index: 20;
  > div {
    top: -0.2rem;
    right: -0.2rem;

    position: absolute;
    width: 10rem;
    z-index: 20;
    display: ${({ show }) => (show ? "flex" : "none")};
  }
`;

const Container = styled(Flex)`
  width: 100%;
  height: ${({ fullHeight = false }) => (fullHeight ? "100%" : "auto")};
  margin: auto auto 1rem auto;
  flex-direction: column;
  input,
  textarea {
    margin: 0;
  }

  .PhoneInput {
    display: flex;
    width: 100%;
    > input {
      width: 100%;
    }
  }
  .PhoneInputCountry {
    display: none;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    select {
      flex: 4;
      width: 100%;
      padding-right: 0;
    }
    svg {
      max-width: 1.5rem;
      flex: 1;
    }
    > div {
      width: 2rem;
    }
  }
`;
const Input = styled.input``;

const FlexContainer = styled(Flex)`
  flex-direction: column;
  width: 100%;
  position: relative;

  transition: all ease-in-out 200ms;

  opacity: ${({ disabled }) => (disabled ? "0.7" : "1")};
  height: ${({ fullHeight = false }) => (fullHeight ? "100%" : "auto")};
  svg {
    position: absolute;
    top: 0.75rem;
    z-index: 0;
    transition: all ease-in 200ms;
    ${({ theme, cta }) => `
      color: ${cta ? "white" : theme.textDark};
      right: ${cta ? "1rem" : "0.5rem"};
    `}
  }

  select {
    ${({ theme }) => `
      background-color: ${theme.backgroundColor};
      color: ${theme.textDark};
      option {
        color: ${theme.textDark} !important;
        background-color: ${theme.backgroundColor} !important;
      }
    `}
  }

  &:last-of-type {
    margin-bottom: 0;
  }
  input,
  textarea {
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "auto")};
  }
  select {
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  }
  textarea {
    height: ${({ fullHeight = false }) => (fullHeight ? "100%" : "6rem")};
    min-height: 6rem;
    resize: none;
  }
  select,
  input,
  textarea {
    -webkit-appearance: none;
    appearance: none;
    padding: 0.5rem;
    border: none;
    border-radius: 0.25rem;
    transform-origin: center;
    transition: all ease-in-out 200ms;

    border: 2px solid ${({ theme }) => theme.borderColor};
    ${({ theme }) => `
      background-color: ${theme.backgroundColor};
      color: ${theme.textDark};
      &:focus, &:hover {
        border-color: ${theme.secondary};
      }
    `}
  }

  select {
    z-index: 1;
    background-color: transparent;
    padding-right: 2rem;
  }
`;

const StyledFormikField = forwardRef(
  (
    {
      isInput = false,
      noLabel = false,
      name,
      labelText,
      description,
      type,
      isLoading = false,
      cta = false,
      required,
      fastField,
      disabled,
      phone = false,
      typeableSelect = false,
      readOnly,
      options,
      reactSelectOptions,
      as,
      fullHeight = false,
      component,
      tooltip,      
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    let Component = isInput ? Input : Field;
    if (fastField) Component = FastField;
    const [field, meta] = useField(name);

    const _ref = useRef(null);
    const [show, setShow] = useState(false);

    const handleClickOutside = useCallback(
      (e) => {
        if (_ref.current && !_ref.current.contains(e.target) && show) {
          setShow(false);
        }
      },
      [_ref, show]
    );

    // Format options for typeable select component
    const selectOptions = options?.map((x) => { return {value: x.key, label: x.key} });

    // Prevent 'Enter' action for react-select
    const handleSelectEnter = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    }

    useEffect(() => {
      if (show) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [handleClickOutside, show]);

    const { setFieldValue } = useFormikContext();

    const { error } = meta || {};

    return (
      <Container {...rest} fullHeight={fullHeight} cta={cta}>
        {(!noLabel || tooltip) && (
          <Label
            required={required}
            mb={!description ? "0.5rem" : "0"}
            htmlFor={name}
          >
            {labelText}
            {tooltip && (
              <>
                <ToolTip
                  onMouseLeave={() => setShow(false)}
                  ref={_ref}
                  onClick={() => setShow(!show)}
                  show={show}
                >
                  <FontAwesomeIcon icon={faCircleInfo} />
                  <Card>
                    {tooltip?.startsWith("http") ? (
                      <img src={tooltip} alt={name} />
                    ) : (
                      <Body>{tooltip}</Body>
                    )}
                  </Card>
                </ToolTip>
              </>
            )}
          </Label>
        )}

        {description && <Helper mb="0.5rem">{description}</Helper>}
        {isLoading ? (
          <ShimmerField />
        ) : (
          <FlexContainer fullHeight={fullHeight} disabled={disabled} cta={cta}>
            {phone ? (
              <PhoneInput
                readOnly={readOnly}
                required={required}
                id={name}
                key={name}
                name={name}
                disabled={disabled}
                type={type}
                ref={ref}
                defaultCountry="US"
                as={as}
                value={field.value || ""}
                // onChange={(e) => { }}
                component={component}
                {...rest}
              />
            ) : ( typeableSelect ? (
              // Use react-select component for a typeableSelect field
              <Select                
                id={`${name}_list`}
                options={reactSelectOptions}
                name={name}                                
                isSearchable={true}                                
                ref={ref}
                {...rest}
                value={{value: field?.value || "", label: field?.value || ""}}
                onKeyDown={handleSelectEnter}                
                blurInputOnSelect
                styles={{ menu: provided => ({ ...provided, zIndex: 9999 }) }}
              />
              
              ) : (
              <Component
                readOnly={readOnly}
                required={required}
                id={name}
                key={name}
                innerRef={ref}
                name={name}
                disabled={disabled}
                type={type}
                options={options}
                // list={typeableSelect ? `${name}_list` : "autocompleteOff"}
                as={as}
                component={component}
                autoComplete="new-password"
                // list="autocompleteOff"
                {...field}
                {...rest}
              /> )
            )
            
            }
            {[as, component].includes("select") && (
              <FontAwesomeIcon icon={faChevronDown} />
            )}
          </FlexContainer>
        )}
        {error && (
          <Body my="0.5rem" color={theme.danger}>
            {error}
          </Body>
        )}
      </Container>
    );
  }
);

export default StyledFormikField;
